import { apiPaths } from "@/lib/api/backendUrls"
import { fetchJson } from "@/lib/api/http"
import type {
  GetNotificationListInput,
  NotificationDto,
  NotificationListResponse,
  UnreadCountDto,
} from "@/entities/notification/types"

const buildListQuery = (input: GetNotificationListInput): string => {
  const params = new URLSearchParams()
  if (input.isRead !== undefined) params.set("IsRead", String(input.isRead))
  if (input.type) params.set("Type", input.type)
  if (input.fromDate) params.set("FromDate", input.fromDate)
  if (input.skipCount !== undefined) params.set("SkipCount", String(input.skipCount))
  if (input.maxResultCount !== undefined) params.set("MaxResultCount", String(input.maxResultCount))
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

export const listNotifications = async (
  accessToken: string,
  input: GetNotificationListInput = {},
): Promise<NotificationListResponse> => {
  return fetchJson<NotificationListResponse>(`${apiPaths.notifications}${buildListQuery(input)}`, {
    method: "GET",
    token: accessToken,
  })
}

/** Ответ агрегата разный по контрактам: camel/Pascal, иногда обёртка `result`. */
export const parseUnreadCountPayload = (raw: unknown): number => {
  const readNum = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return Math.max(0, Math.floor(v))
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v)
      return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null
    }
    return null
  }

  let cur: unknown = raw
  if (typeof cur === "object" && cur !== null) {
    const r = (cur as Record<string, unknown>).result
    if (r !== undefined) cur = r
  }
  const n0 = readNum(cur)
  if (n0 !== null) return n0

  if (typeof cur !== "object" || cur === null) return 0
  const o = cur as Record<string, unknown>
  for (const key of ["count", "Count", "unreadCount", "UnreadCount"]) {
    const n = readNum(o[key])
    if (n !== null) return n
  }
  return 0
}

export const getNotificationUnreadCount = async (accessToken: string): Promise<UnreadCountDto> => {
  const raw = await fetchJson<unknown>(apiPaths.notificationUnreadCount, {
    method: "GET",
    token: accessToken,
  })
  return { count: parseUnreadCountPayload(raw) }
}

export const markNotificationRead = async (accessToken: string, id: string): Promise<void> => {
  await fetchJson<unknown>(apiPaths.notificationMarkRead.replace("{id}", encodeURIComponent(id)), {
    method: "POST",
    token: accessToken,
  })
}

export const markAllNotificationsRead = async (accessToken: string): Promise<void> => {
  await fetchJson<unknown>(apiPaths.notificationMarkAllRead, {
    method: "POST",
    token: accessToken,
  })
}

export const deleteNotification = async (accessToken: string, id: string): Promise<void> => {
  await fetchJson<unknown>(apiPaths.notificationById.replace("{id}", encodeURIComponent(id)), {
    method: "DELETE",
    token: accessToken,
  })
}

export const deleteAllReadNotifications = async (accessToken: string): Promise<void> => {
  await fetchJson<unknown>(apiPaths.notificationAllRead, {
    method: "DELETE",
    token: accessToken,
  })
}

/** Собирает DTO из SSE-фрейма: сервер может отдавать либо чистый DTO, либо обёртку. */
export const parseSseNotification = (raw: string): NotificationDto | null => {
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== "object" || parsed === null) return null
    const dto = parsed as NotificationDto
    if (typeof dto.id !== "string" || typeof dto.eventId !== "number") return null
    return dto
  } catch {
    return null
  }
}
